import React, { useState } from "react";
import useSWR from "swr";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { LogoMark } from "@/components/app/Logo";
import {
  getProject, updateProject, getAccounting, createPayment, deletePayment,
  listPermits, createPermit, updatePermit, deletePermit,
  listDrawings, uploadDrawing, deleteDrawing, drawingDownloadUrl,
  listChangeOrders, createChangeOrder, updateChangeOrder, deleteChangeOrder,
  listSnags, createSnag, updateSnag, deleteSnag,
  listCatalogs, createVendorForProject, closeBoq, reopenBoq, uploadCover, useCachedCover,
} from "@/lib/projectApi";
import { fmtMoney, fmtDate } from "@/lib/format";
import {
  ArrowLeft, Home, Building, MapPin, Loader2, Plus, Trash2, Upload, FileText,
  Download, DollarSign, ScrollText, ClipboardCheck, ClipboardList, HardHat, Wallet,
  ArrowUpRight, Check, X, Edit3, CheckCircle2, ExternalLink,
} from "lucide-react";
import { toast } from "sonner";

const PHASE_TONE = {
  "Concept": "bg-slate-500/10 text-slate-700 border-slate-500/40",
  "Design Development": "bg-blue-500/10 text-blue-700 border-blue-500/40",
  "Tender": "bg-purple-500/10 text-purple-700 border-purple-500/40",
  "Execution": "bg-emerald-500/10 text-emerald-700 border-emerald-500/40",
  "Snagging": "bg-amber-500/10 text-amber-700 border-amber-500/40",
  "Handover": "bg-teal-500/10 text-teal-700 border-teal-500/40",
};

const STATUS_TONE = {
  not_started: "bg-slate-500/10 text-slate-700 border-slate-500/40",
  submitted: "bg-blue-500/10 text-blue-700 border-blue-500/40",
  in_review: "bg-amber-500/10 text-amber-700 border-amber-500/40",
  approved: "bg-emerald-500/10 text-emerald-700 border-emerald-500/40",
  rejected: "bg-red-500/10 text-red-700 border-red-500/40",
  expired: "bg-gray-500/10 text-gray-700 border-gray-500/40",
  for_review: "bg-blue-500/10 text-blue-700 border-blue-500/40",
  superseded: "bg-slate-500/10 text-slate-500 border-slate-500/30",
  pending: "bg-amber-500/10 text-amber-700 border-amber-500/40",
  open: "bg-red-500/10 text-red-700 border-red-500/40",
  in_progress: "bg-amber-500/10 text-amber-700 border-amber-500/40",
  closed: "bg-emerald-500/10 text-emerald-700 border-emerald-500/40",
};

const humanize = (s) => (s || "").replace(/_/g, " ").replace(/\b\w/g, m => m.toUpperCase());

export default function ProjectDetail() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { data: project, mutate } = useSWR(projectId ? `/projects/${projectId}` : null, () => getProject(projectId));
  const { data: catalogs } = useSWR("/catalogs", listCatalogs);
  const [editOpen, setEditOpen] = useState(false);
  const cover = useCachedCover(project?.id, project?.cover_id);

  if (!project) return <div className="min-h-screen grid place-items-center"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;

  const TypeIcon = project.project_type === "Residential" ? Home : Building;
  const phaseCls = PHASE_TONE[project.phase] || "bg-slate-500/10 text-slate-700 border-slate-500/40";

  const handleCoverPick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Please choose an image (JPG, PNG or WEBP)"); return; }
    const fd = new FormData();
    fd.append("file", f);
    try {
      // Purge any cached data-URL for the old cover so the new one is fetched
      if (project.cover_id) {
        try { localStorage.removeItem(`nh_cover_${project.cover_id}`); } catch {}
      }
      await uploadCover(project.id, fd);
      toast.success("Cover updated");
      mutate();
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
    e.target.value = "";
  };

  return (
    <div className="min-h-screen">
      <header className="sticky top-3 z-40 mx-auto max-w-7xl px-3 sm:px-4 lg:px-6">
        <div className="glass rounded-full px-4 py-2 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 min-w-0">
            <Link to="/projects" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-primary shrink-0">
              <ArrowLeft className="h-3.5 w-3.5" /> Projects
            </Link>
            <div className="h-4 w-px bg-border/60 mx-1" />
            <div className="flex items-center gap-1.5 min-w-0">
              <TypeIcon className="h-3.5 w-3.5 text-primary shrink-0" />
              <span className="font-heading text-sm font-bold truncate">{project.name}</span>
            </div>
            <div className="hidden md:flex items-center gap-1.5 ml-2">
              <Badge variant="outline" className="text-[10px] bg-white/60 backdrop-blur">{project.project_type}</Badge>
              <Badge variant="outline" className={`text-[10px] bg-white/60 backdrop-blur ${phaseCls}`}>{project.phase}</Badge>
            </div>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            <label className="cursor-pointer">
              <input type="file" accept="image/*" className="hidden" onChange={handleCoverPick} data-testid="input-cover-image" />
              <Button variant="ghost" size="sm" asChild className="h-8 rounded-full text-xs" data-testid="btn-upload-cover">
                <span><Upload className="h-3.5 w-3.5 mr-1" /> {cover ? "Change cover" : "Add cover"}</span>
              </Button>
            </label>
            <EditProjectDialog open={editOpen} setOpen={setEditOpen} project={project} catalogs={catalogs} onSaved={mutate} />
          </div>
        </div>
      </header>

      {/* Cover hero (image only) + info card below */}
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 mt-4 space-y-4">
        {cover ? (
          <div className="relative overflow-hidden rounded-3xl border border-white/40 card-glossy aspect-[16/5]">
            <img src={cover} alt="" className="w-full h-full object-cover" />
          </div>
        ) : (
          <div className="relative overflow-hidden rounded-3xl border border-white/40 card-glossy aspect-[16/5] bg-gradient-to-br from-primary/20 via-accent/15 to-primary/10 grid place-items-center">
            <TypeIcon className="h-14 w-14 text-primary/40" />
          </div>
        )}

        <div className="rounded-2xl border border-border bg-white/95 shadow-sm p-5 card-glossy">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-start gap-4">
              <div className="grid h-11 w-11 place-items-center rounded-xl bg-primary/15 border border-primary/40 shrink-0">
                <TypeIcon className="h-5 w-5 text-primary" />
              </div>
              <div>
                <h1 className="font-heading text-xl sm:text-2xl font-bold">{project.name}</h1>
                <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground flex-wrap">
                  {project.client_name && <span>Client: <b className="text-foreground">{project.client_name}</b></span>}
                  {project.client_phone && <span>· {project.client_phone}</span>}
                  {project.client_email && <span>· {project.client_email}</span>}
                </div>
                <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
                  <MapPin className="h-3 w-3" /> {project.address || project.location}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-1.5">
              <Badge variant="outline" className="text-[10px]">{project.project_type}</Badge>
              <Badge variant="outline" className={`text-[10px] ${phaseCls}`}>{project.phase}</Badge>
            </div>
          </div>
        </div>
      </div>

      <main className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-6">
        <Tabs defaultValue="overview" className="w-full">
          <TabsList className="tabs-glossy gap-1 p-1" data-testid="project-tabs">
            <TabsTrigger value="overview" className="gap-1.5" data-testid="tab-overview"><ClipboardList className="h-4 w-4" />Overview</TabsTrigger>
            <TabsTrigger value="vendors" className="gap-1.5" data-testid="tab-vendors"><HardHat className="h-4 w-4" />Vendors & BOQs</TabsTrigger>
            <TabsTrigger value="accounting" className="gap-1.5" data-testid="tab-accounting"><Wallet className="h-4 w-4" />Accounting</TabsTrigger>
            <TabsTrigger value="drawings" className="gap-1.5" data-testid="tab-drawings"><FileText className="h-4 w-4" />Drawings & Files</TabsTrigger>
            <TabsTrigger value="permits" className="gap-1.5" data-testid="tab-permits"><ScrollText className="h-4 w-4" />Permits & NOC</TabsTrigger>
            <TabsTrigger value="change-orders" className="gap-1.5" data-testid="tab-cos"><Edit3 className="h-4 w-4" />Change Orders</TabsTrigger>
            <TabsTrigger value="snags" className="gap-1.5" data-testid="tab-snags"><ClipboardCheck className="h-4 w-4" />Snag List</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="mt-6"><OverviewTab project={project} /></TabsContent>
          <TabsContent value="vendors" className="mt-6"><VendorsTab project={project} onChange={mutate} catalogs={catalogs} /></TabsContent>
          <TabsContent value="accounting" className="mt-6"><AccountingTab project={project} catalogs={catalogs} /></TabsContent>
          <TabsContent value="drawings" className="mt-6"><DrawingsTab project={project} catalogs={catalogs} /></TabsContent>
          <TabsContent value="permits" className="mt-6"><PermitsTab project={project} catalogs={catalogs} /></TabsContent>
          <TabsContent value="change-orders" className="mt-6"><ChangeOrdersTab project={project} /></TabsContent>
          <TabsContent value="snags" className="mt-6"><SnagsTab project={project} catalogs={catalogs} /></TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

/* -------------------- OVERVIEW -------------------- */
function OverviewTab({ project }) {
  const { data: acct } = useSWR(`/projects/${project.id}/accounting`, () => getAccounting(project.id));
  const { data: permits } = useSWR(`/projects/${project.id}/permits`, () => listPermits(project.id));
  const totals = acct?.totals || { awarded: 0, paid: 0, outstanding: 0 };
  const pendingPermits = (permits || []).filter(p => p.status !== "approved" && p.status !== "expired");
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
      <div className="kpi-card card-glossy">
        <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">Contract value (target)</div>
        <div className="num text-2xl font-bold mt-1">{fmtMoney(project.contract_value || 0)}</div>
        <div className="text-xs text-muted-foreground mt-1">Awarded so far <b className="text-foreground num">{fmtMoney(totals.awarded)}</b></div>
      </div>
      <div className="kpi-card card-glossy">
        <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">Paid to vendors</div>
        <div className="num text-2xl font-bold mt-1 text-emerald-700">{fmtMoney(totals.paid)}</div>
        <div className="text-xs text-muted-foreground mt-1">Across {project.vendors?.length || 0} vendor{(project.vendors?.length || 0) !== 1 ? "s" : ""}</div>
      </div>
      <div className="kpi-card card-glossy">
        <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">Outstanding</div>
        <div className="num text-2xl font-bold mt-1 text-amber-700">{fmtMoney(totals.outstanding)}</div>
        <div className="text-xs text-muted-foreground mt-1">{pendingPermits.length} permit{pendingPermits.length !== 1 ? "s" : ""} pending</div>
      </div>
      {project.notes && (
        <div className="md:col-span-3 rounded-2xl border border-border bg-card/70 backdrop-blur-xl p-5 card-glossy">
          <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground mb-2">Notes</div>
          <p className="text-sm whitespace-pre-wrap">{project.notes}</p>
        </div>
      )}
    </div>
  );
}

/* -------------------- VENDORS + BOQs -------------------- */
function VendorsTab({ project, onChange, catalogs }) {
  const navigate = useNavigate();
  const vendorTypes = catalogs?.vendor_types || [];
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", vendor_type: "Main Contractor", description: "", contact_email: "", contact_phone: "", city: "Dubai, UAE" });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!form.name.trim()) { toast.error("Vendor name required"); return; }
    setSaving(true);
    try {
      const v = await createVendorForProject(project.id, form);
      toast.success(`Added ${v.name}`);
      setOpen(false);
      setForm({ name: "", vendor_type: "Main Contractor", description: "", contact_email: "", contact_phone: "", city: "Dubai, UAE" });
      onChange();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="font-heading text-lg font-bold">Vendors ({project.vendors?.length || 0})</h2>
          <p className="text-sm text-muted-foreground">Every trade for this project — click a vendor to upload BOQs and lock the awarded price.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="btn-add-vendor"><Plus className="h-4 w-4 mr-1" /> Add vendor</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Add vendor</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Vendor name</Label><Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Cherwell" autoFocus data-testid="input-vendor-name" /></div>
              <div>
                <Label>Trade / type</Label>
                <Select value={form.vendor_type} onValueChange={v => setForm(f => ({ ...f, vendor_type: v }))}>
                  <SelectTrigger data-testid="select-vendor-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {vendorTypes.map(o => <SelectItem key={o.id} value={o.value}>{o.value}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Description</Label><Textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Premium joinery & fit-out contractor…" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Email</Label><Input value={form.contact_email} onChange={e => setForm(f => ({ ...f, contact_email: e.target.value }))} /></div>
                <div><Label>Phone</Label><Input value={form.contact_phone} onChange={e => setForm(f => ({ ...f, contact_phone: e.target.value }))} /></div>
              </div>
              <div><Label>City</Label><Input value={form.city} onChange={e => setForm(f => ({ ...f, city: e.target.value }))} /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={submit} disabled={saving} data-testid="btn-submit-vendor">{saving ? "Adding…" : "Add vendor"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {!project.vendors?.length ? (
        <div className="rounded-2xl border border-dashed border-border p-12 text-center bg-card/50 backdrop-blur">
          <HardHat className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
          <p className="text-muted-foreground">No vendors yet. Add the first trade to this project.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {project.vendors.map(v => (
            <button
              key={v.id}
              onClick={() => navigate(`/vendors/${v.id}`)}
              className="text-left rounded-2xl border border-border bg-card/70 backdrop-blur-xl hover:border-primary/50 p-5 transition card-glossy group"
              data-testid={`vendor-card-${v.id}`}
            >
              <div className="flex items-start justify-between gap-2 mb-1">
                <Badge variant="outline" className="text-[10px]">{v.vendor_type || "Contractor"}</Badge>
                <ArrowUpRight className="h-4 w-4 text-muted-foreground group-hover:text-primary transition" />
              </div>
              <h3 className="font-heading text-lg font-bold group-hover:text-primary transition">{v.name}</h3>
              {v.description && <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{v.description}</p>}
              <div className="mt-3 pt-3 border-t border-border/60 grid grid-cols-3 gap-2 text-xs">
                <div><div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">BOQs</div><div className="num font-bold">{v.boq_count}</div></div>
                <div><div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">Awarded</div><div className="num font-bold">{fmtMoney(v.awarded_total || 0, false)}</div></div>
                <div><div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">Paid</div><div className="num font-bold text-emerald-700">{fmtMoney(v.paid_total || 0, false)}</div></div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* -------------------- ACCOUNTING -------------------- */
function AccountingTab({ project, catalogs }) {
  const { data: acct, mutate } = useSWR(`/projects/${project.id}/accounting`, () => getAccounting(project.id));
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ vendor_id: "", boq_id: "", amount: 0, payment_date: new Date().toISOString().slice(0, 10), milestone: "", method: "Bank transfer", reference: "", notes: "" });
  const [saving, setSaving] = useState(false);
  const [preselectVendor, setPreselectVendor] = useState(null);

  const openForVendor = (vid) => {
    setForm(f => ({ ...f, vendor_id: vid, boq_id: "" }));
    setPreselectVendor(vid);
    setOpen(true);
  };

  const submit = async () => {
    if (!form.amount) { toast.error("Enter an amount"); return; }
    setSaving(true);
    try {
      await createPayment(project.id, { ...form, amount: Number(form.amount), direction: "out" });
      toast.success("Payment recorded");
      setOpen(false);
      setForm({ vendor_id: "", boq_id: "", amount: 0, payment_date: new Date().toISOString().slice(0, 10), milestone: "", method: "Bank transfer", reference: "", notes: "" });
      mutate();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this payment record?")) return;
    try { await deletePayment(id); mutate(); } catch { toast.error("Failed"); }
  };

  if (!acct) return <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;

  const totals = acct.totals;
  const currentVendor = acct.vendors.find(v => v.vendor_id === form.vendor_id);
  const methods = catalogs?.payment_methods || [];
  const milestones = catalogs?.payment_milestones || [];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="kpi-card card-glossy">
          <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">Total awarded</div>
          <div className="num text-2xl font-bold mt-1">{fmtMoney(totals.awarded)}</div>
        </div>
        <div className="kpi-card card-glossy">
          <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">Total paid</div>
          <div className="num text-2xl font-bold mt-1 text-emerald-700">{fmtMoney(totals.paid)}</div>
        </div>
        <div className="kpi-card card-glossy">
          <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">Outstanding</div>
          <div className="num text-2xl font-bold mt-1 text-amber-700">{fmtMoney(totals.outstanding)}</div>
        </div>
      </div>

      <div className="rounded-2xl border border-border bg-card/70 backdrop-blur-xl p-5 card-glossy">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <h3 className="font-heading text-base font-bold">By vendor</h3>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button size="sm" data-testid="btn-record-payment"><Plus className="h-4 w-4 mr-1" /> Record payment</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Record vendor payment</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label>Vendor</Label>
                  <Select value={form.vendor_id} onValueChange={v => setForm(f => ({ ...f, vendor_id: v, boq_id: "" }))}>
                    <SelectTrigger data-testid="select-payment-vendor"><SelectValue placeholder="Choose vendor" /></SelectTrigger>
                    <SelectContent>
                      {acct.vendors.map(v => <SelectItem key={v.vendor_id} value={v.vendor_id}>{v.vendor_name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                {currentVendor && currentVendor.boqs.length > 0 && (
                  <div>
                    <Label>Against BOQ (optional)</Label>
                    <Select value={form.boq_id || "none"} onValueChange={v => setForm(f => ({ ...f, boq_id: v === "none" ? "" : v }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">— none —</SelectItem>
                        {currentVendor.boqs.map(b => <SelectItem key={b.id} value={b.id}>{b.quotation_no || b.id.slice(0, 8)}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Amount (AED)</Label><Input type="number" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} data-testid="input-payment-amount" /></div>
                  <div><Label>Date</Label><Input type="date" value={form.payment_date} onChange={e => setForm(f => ({ ...f, payment_date: e.target.value }))} /></div>
                </div>
                <div>
                  <Label>Milestone</Label>
                  <Select value={form.milestone || "custom"} onValueChange={v => setForm(f => ({ ...f, milestone: v === "custom" ? "" : v }))}>
                    <SelectTrigger><SelectValue placeholder="Choose milestone" /></SelectTrigger>
                    <SelectContent>
                      {milestones.map(o => <SelectItem key={o.id} value={o.value}>{o.value}</SelectItem>)}
                      <SelectItem value="custom">Custom / other</SelectItem>
                    </SelectContent>
                  </Select>
                  {form.milestone === "" && <Input className="mt-2" value={form.milestone} onChange={e => setForm(f => ({ ...f, milestone: e.target.value }))} placeholder="Custom milestone" />}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Method</Label>
                    <Select value={form.method} onValueChange={v => setForm(f => ({ ...f, method: v }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {methods.map(o => <SelectItem key={o.id} value={o.value}>{o.value}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label>Reference</Label><Input value={form.reference} onChange={e => setForm(f => ({ ...f, reference: e.target.value }))} placeholder="Cheque #, TX ref…" /></div>
                </div>
                <div><Label>Notes</Label><Textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} /></div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button onClick={submit} disabled={saving} data-testid="btn-submit-payment">{saving ? "Saving…" : "Record payment"}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
        {acct.vendors.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4">Award a BOQ first (open a vendor → open a BOQ → "Award" button).</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/40">
                <tr className="text-left">
                  <th className="px-3 py-2 font-mono text-[11px] uppercase tracking-wider text-muted-foreground">Vendor</th>
                  <th className="px-3 py-2 font-mono text-[11px] uppercase tracking-wider text-muted-foreground text-right">Awarded</th>
                  <th className="px-3 py-2 font-mono text-[11px] uppercase tracking-wider text-muted-foreground text-right">Paid</th>
                  <th className="px-3 py-2 font-mono text-[11px] uppercase tracking-wider text-muted-foreground text-right">Outstanding</th>
                  <th className="px-3 py-2 font-mono text-[11px] uppercase tracking-wider text-muted-foreground text-right">Progress</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {acct.vendors.map(v => {
                  const pct = v.awarded > 0 ? Math.min(100, (v.paid / v.awarded) * 100) : 0;
                  return (
                    <tr key={v.vendor_id} className="border-t border-border">
                      <td className="px-3 py-2 font-medium">{v.vendor_name}</td>
                      <td className="px-3 py-2 text-right num">{fmtMoney(v.awarded, false)}</td>
                      <td className="px-3 py-2 text-right num text-emerald-700">{fmtMoney(v.paid, false)}</td>
                      <td className="px-3 py-2 text-right num text-amber-700">{fmtMoney(v.outstanding, false)}</td>
                      <td className="px-3 py-2 text-right">
                        <div className="inline-flex items-center gap-2">
                          <div className="w-24 h-1.5 rounded-full bg-muted overflow-hidden">
                            <div className="h-full bg-emerald-500" style={{ width: `${pct}%` }} />
                          </div>
                          <span className="text-xs num">{pct.toFixed(0)}%</span>
                        </div>
                      </td>
                      <td className="px-3 py-2 text-right">
                        <Button size="sm" variant="outline" onClick={() => openForVendor(v.vendor_id)}>
                          <Plus className="h-3 w-3 mr-1" /> Payment
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="rounded-2xl border border-border bg-card/70 backdrop-blur-xl p-5 card-glossy">
        <h3 className="font-heading text-base font-bold mb-3">Payment history ({acct.payments.length})</h3>
        {acct.payments.length === 0 ? (
          <p className="text-sm text-muted-foreground">No payments recorded yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/40">
                <tr className="text-left">
                  <th className="px-3 py-2 font-mono text-[11px] uppercase tracking-wider text-muted-foreground">Date</th>
                  <th className="px-3 py-2 font-mono text-[11px] uppercase tracking-wider text-muted-foreground">Vendor</th>
                  <th className="px-3 py-2 font-mono text-[11px] uppercase tracking-wider text-muted-foreground">Milestone</th>
                  <th className="px-3 py-2 font-mono text-[11px] uppercase tracking-wider text-muted-foreground">Method</th>
                  <th className="px-3 py-2 font-mono text-[11px] uppercase tracking-wider text-muted-foreground">Reference</th>
                  <th className="px-3 py-2 font-mono text-[11px] uppercase tracking-wider text-muted-foreground text-right">Amount</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {acct.payments.map(p => {
                  const vendor = acct.vendors.find(v => v.vendor_id === p.vendor_id);
                  return (
                    <tr key={p.id} className="border-t border-border">
                      <td className="px-3 py-2">{p.payment_date}</td>
                      <td className="px-3 py-2">{vendor?.vendor_name || "—"}</td>
                      <td className="px-3 py-2 text-muted-foreground">{p.milestone || "—"}</td>
                      <td className="px-3 py-2 text-muted-foreground">{p.method}</td>
                      <td className="px-3 py-2 text-muted-foreground text-xs">{p.reference}</td>
                      <td className="px-3 py-2 text-right num font-bold">{fmtMoney(p.amount, false)}</td>
                      <td className="px-3 py-2 text-right">
                        <Button size="sm" variant="ghost" onClick={() => remove(p.id)}><Trash2 className="h-3.5 w-3.5" /></Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

/* -------------------- DRAWINGS -------------------- */
function DrawingsTab({ project, catalogs }) {
  const { data: drawings, mutate } = useSWR(`/projects/${project.id}/drawings`, () => listDrawings(project.id));
  const categories = catalogs?.drawing_categories || [];
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [form, setForm] = useState({ name: "", category: "Architectural", revision: "R0", status: "for_review" });
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState("all");

  const submit = async () => {
    if (!file) { toast.error("Choose a file"); return; }
    setSaving(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("name", form.name || file.name);
    fd.append("category", form.category);
    fd.append("revision", form.revision);
    fd.append("status", form.status);
    try {
      await uploadDrawing(project.id, fd);
      toast.success(`Uploaded ${file.name}`);
      setOpen(false); setFile(null); setForm({ name: "", category: "Architectural", revision: "R0", status: "for_review" });
      mutate();
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this drawing?")) return;
    try { await deleteDrawing(id); mutate(); } catch { toast.error("Failed"); }
  };

  const items = (drawings || []).filter(d => filter === "all" || d.category === filter);
  const byCategory = categories.map(c => ({ ...c, count: (drawings || []).filter(d => d.category === c.value).length }));

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="font-heading text-lg font-bold">Drawings, 3D models & documents</h2>
          <p className="text-sm text-muted-foreground">Upload architectural drawings, MEP, joinery, 3D files, permits, contracts — up to 60 MB each.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="btn-upload-drawing"><Upload className="h-4 w-4 mr-1" /> Upload file</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Upload file</DialogTitle><DialogDescription>PDF, DWG, images, 3D models (up to 60 MB).</DialogDescription></DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>File</Label>
                <Input type="file" onChange={e => setFile(e.target.files?.[0] || null)} data-testid="input-drawing-file" />
                {file && <p className="text-[11px] text-muted-foreground mt-1">{file.name} · {(file.size / 1024 / 1024).toFixed(2)} MB</p>}
              </div>
              <div><Label>Display name (optional)</Label><Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Ground floor plan" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Category</Label>
                  <Select value={form.category} onValueChange={v => setForm(f => ({ ...f, category: v }))}>
                    <SelectTrigger data-testid="select-drawing-category"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {categories.map(o => <SelectItem key={o.id} value={o.value}>{o.value}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Revision</Label><Input value={form.revision} onChange={e => setForm(f => ({ ...f, revision: e.target.value }))} placeholder="R0" /></div>
              </div>
              <div>
                <Label>Status</Label>
                <Select value={form.status} onValueChange={v => setForm(f => ({ ...f, status: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="for_review">For review</SelectItem>
                    <SelectItem value="approved">Approved</SelectItem>
                    <SelectItem value="superseded">Superseded</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={submit} disabled={saving} data-testid="btn-submit-drawing">{saving ? "Uploading…" : "Upload"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="flex flex-wrap gap-1.5">
        <button onClick={() => setFilter("all")} className={`text-xs px-3 py-1 rounded-full border ${filter === "all" ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border hover:border-primary/50"}`}>All ({drawings?.length || 0})</button>
        {byCategory.filter(c => c.count > 0).map(c => (
          <button key={c.id} onClick={() => setFilter(c.value)} className={`text-xs px-3 py-1 rounded-full border ${filter === c.value ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border hover:border-primary/50"}`}>
            {c.value} ({c.count})
          </button>
        ))}
      </div>

      {!drawings ? <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
        : items.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-border p-12 text-center bg-card/40">
            <FileText className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
            <p className="text-muted-foreground">No files yet in this category.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {items.map(d => {
              const isImg = (d.content_type || "").startsWith("image/");
              const url = drawingDownloadUrl(d.id);
              const st = STATUS_TONE[d.status] || "";
              return (
                <div key={d.id} className="rounded-xl border border-border bg-card/70 backdrop-blur-xl p-3 card-glossy" data-testid={`drawing-${d.id}`}>
                  <div className="aspect-video bg-muted/40 rounded-lg overflow-hidden mb-2 grid place-items-center">
                    {isImg ? <img src={url} alt={d.name} className="w-full h-full object-cover" /> : <FileText className="h-10 w-10 text-muted-foreground" />}
                  </div>
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <Badge variant="outline" className="text-[10px] truncate max-w-full">{d.category}</Badge>
                    <Badge variant="outline" className={`text-[10px] ${st}`}>{humanize(d.status)}</Badge>
                  </div>
                  <div className="font-medium text-sm truncate" title={d.name}>{d.name}</div>
                  <div className="text-[10px] text-muted-foreground font-mono uppercase tracking-wider mt-0.5">{d.revision} · {(d.size / 1024).toFixed(0)} KB · {fmtDate(d.uploaded_at)}</div>
                  <div className="flex gap-1 mt-2">
                    <a href={url} target="_blank" rel="noreferrer" className="flex-1"><Button size="sm" variant="outline" className="w-full"><Download className="h-3.5 w-3.5 mr-1" /> Open</Button></a>
                    <Button size="sm" variant="ghost" onClick={() => remove(d.id)}><Trash2 className="h-3.5 w-3.5" /></Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
    </div>
  );
}

/* -------------------- PERMITS -------------------- */
function PermitsTab({ project, catalogs }) {
  const { data: permits, mutate } = useSWR(`/projects/${project.id}/permits`, () => listPermits(project.id));
  const authorities = catalogs?.authorities || [];
  const statuses = catalogs?.permit_statuses || [];
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ authority: "", permit_type: "", reference_no: "", status: "not_started", submitted_at: "", expected_by: "", notes: "" });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!form.authority) { toast.error("Choose an authority"); return; }
    setSaving(true);
    try {
      await createPermit(project.id, form);
      toast.success(`Added ${form.authority}`);
      setOpen(false);
      setForm({ authority: "", permit_type: "", reference_no: "", status: "not_started", submitted_at: "", expected_by: "", notes: "" });
      mutate();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  const setStatus = async (id, status) => {
    try { await updatePermit(id, { status }); mutate(); } catch { toast.error("Failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this permit entry?")) return;
    try { await deletePermit(id); mutate(); } catch { toast.error("Failed"); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="font-heading text-lg font-bold">Permits, NOCs & approvals</h2>
          <p className="text-sm text-muted-foreground">Track every submission across Dubai authorities & freehold developers.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="btn-add-permit"><Plus className="h-4 w-4 mr-1" /> Add permit</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Add permit / NOC</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Authority</Label>
                <Select value={form.authority} onValueChange={v => setForm(f => ({ ...f, authority: v }))}>
                  <SelectTrigger data-testid="select-permit-authority"><SelectValue placeholder="Choose authority" /></SelectTrigger>
                  <SelectContent>{authorities.map(o => <SelectItem key={o.id} value={o.value}>{o.value}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Permit type / scope</Label><Input value={form.permit_type} onChange={e => setForm(f => ({ ...f, permit_type: e.target.value }))} placeholder="e.g. Interior modification" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Reference #</Label><Input value={form.reference_no} onChange={e => setForm(f => ({ ...f, reference_no: e.target.value }))} placeholder="TRK-2026-…" /></div>
                <div>
                  <Label>Status</Label>
                  <Select value={form.status} onValueChange={v => setForm(f => ({ ...f, status: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{statuses.map(o => <SelectItem key={o.id} value={o.value}>{humanize(o.value)}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Submitted on</Label><Input type="date" value={form.submitted_at} onChange={e => setForm(f => ({ ...f, submitted_at: e.target.value }))} /></div>
                <div><Label>Expected approval</Label><Input type="date" value={form.expected_by} onChange={e => setForm(f => ({ ...f, expected_by: e.target.value }))} /></div>
              </div>
              <div><Label>Notes</Label><Textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={submit} disabled={saving} data-testid="btn-submit-permit">{saving ? "Adding…" : "Add"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {!permits ? <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
        : permits.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-border p-12 text-center bg-card/40">
            <ScrollText className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
            <p className="text-muted-foreground">No permits tracked yet. Add DDA, Trakheesi, Emaar NOC, DEWA…</p>
          </div>
        ) : (
          <div className="rounded-2xl border border-border bg-card/70 backdrop-blur-xl overflow-hidden card-glossy">
            <table className="w-full text-sm">
              <thead className="bg-muted/40">
                <tr className="text-left">
                  <th className="px-4 py-2.5 font-mono text-[11px] uppercase tracking-wider text-muted-foreground">Authority</th>
                  <th className="px-4 py-2.5 font-mono text-[11px] uppercase tracking-wider text-muted-foreground">Type</th>
                  <th className="px-4 py-2.5 font-mono text-[11px] uppercase tracking-wider text-muted-foreground">Ref #</th>
                  <th className="px-4 py-2.5 font-mono text-[11px] uppercase tracking-wider text-muted-foreground">Status</th>
                  <th className="px-4 py-2.5 font-mono text-[11px] uppercase tracking-wider text-muted-foreground">Submitted</th>
                  <th className="px-4 py-2.5 font-mono text-[11px] uppercase tracking-wider text-muted-foreground">Expected</th>
                  <th className="px-4 py-2.5"></th>
                </tr>
              </thead>
              <tbody>
                {permits.map(p => (
                  <tr key={p.id} className="border-t border-border">
                    <td className="px-4 py-2.5 font-medium">{p.authority}</td>
                    <td className="px-4 py-2.5 text-muted-foreground">{p.permit_type || "—"}</td>
                    <td className="px-4 py-2.5 font-mono text-xs">{p.reference_no || "—"}</td>
                    <td className="px-4 py-2.5">
                      <Select value={p.status} onValueChange={v => setStatus(p.id, v)}>
                        <SelectTrigger className="h-7 w-[140px] text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>{statuses.map(o => <SelectItem key={o.id} value={o.value}>{humanize(o.value)}</SelectItem>)}</SelectContent>
                      </Select>
                    </td>
                    <td className="px-4 py-2.5 text-xs">{p.submitted_at || "—"}</td>
                    <td className="px-4 py-2.5 text-xs">{p.expected_by || "—"}</td>
                    <td className="px-4 py-2.5 text-right">
                      <Button size="sm" variant="ghost" onClick={() => remove(p.id)}><Trash2 className="h-3.5 w-3.5" /></Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
    </div>
  );
}

/* -------------------- CHANGE ORDERS -------------------- */
function ChangeOrdersTab({ project }) {
  const { data: cos, mutate } = useSWR(`/projects/${project.id}/cos`, () => listChangeOrders(project.id));
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ vendor_id: "", description: "", amount: 0, status: "pending", dated: new Date().toISOString().slice(0, 10), notes: "" });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!form.description.trim()) { toast.error("Describe the change"); return; }
    setSaving(true);
    try {
      await createChangeOrder(project.id, { ...form, amount: Number(form.amount) });
      toast.success("Change order created");
      setOpen(false);
      setForm({ vendor_id: "", description: "", amount: 0, status: "pending", dated: new Date().toISOString().slice(0, 10), notes: "" });
      mutate();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  const setStatus = async (id, status) => { try { await updateChangeOrder(id, { status }); mutate(); } catch { toast.error("Failed"); } };
  const remove = async (id) => { if (!window.confirm("Delete this CO?")) return; try { await deleteChangeOrder(id); mutate(); } catch { toast.error("Failed"); } };

  const totalApproved = (cos || []).filter(c => c.status === "approved").reduce((s, c) => s + Number(c.amount || 0), 0);
  const totalPending = (cos || []).filter(c => c.status === "pending").reduce((s, c) => s + Number(c.amount || 0), 0);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="kpi-card card-glossy">
          <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">Approved change orders</div>
          <div className="num text-2xl font-bold mt-1 text-emerald-700">{fmtMoney(totalApproved)}</div>
        </div>
        <div className="kpi-card card-glossy">
          <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">Pending decisions</div>
          <div className="num text-2xl font-bold mt-1 text-amber-700">{fmtMoney(totalPending)}</div>
        </div>
      </div>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="font-heading text-lg font-bold">Variations & change orders</h2>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="btn-add-co"><Plus className="h-4 w-4 mr-1" /> New change order</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>New change order</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Vendor</Label>
                <Select value={form.vendor_id || "none"} onValueChange={v => setForm(f => ({ ...f, vendor_id: v === "none" ? "" : v }))}>
                  <SelectTrigger><SelectValue placeholder="Optional" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— none —</SelectItem>
                    {project.vendors.map(v => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Description</Label><Textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="e.g. Extend joinery in study" data-testid="input-co-desc" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Amount (AED)</Label><Input type="number" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} /></div>
                <div><Label>Date</Label><Input type="date" value={form.dated} onChange={e => setForm(f => ({ ...f, dated: e.target.value }))} /></div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={submit} disabled={saving}>{saving ? "Saving…" : "Save"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      {!cos ? <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
        : cos.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-border p-12 text-center bg-card/40">
            <Edit3 className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
            <p className="text-muted-foreground">No variations yet.</p>
          </div>
        ) : (
          <div className="rounded-2xl border border-border bg-card/70 backdrop-blur-xl overflow-hidden card-glossy">
            <table className="w-full text-sm">
              <thead className="bg-muted/40"><tr className="text-left">
                <th className="px-4 py-2.5 font-mono text-[11px] uppercase text-muted-foreground">CO #</th>
                <th className="px-4 py-2.5 font-mono text-[11px] uppercase text-muted-foreground">Vendor</th>
                <th className="px-4 py-2.5 font-mono text-[11px] uppercase text-muted-foreground">Description</th>
                <th className="px-4 py-2.5 font-mono text-[11px] uppercase text-muted-foreground">Date</th>
                <th className="px-4 py-2.5 font-mono text-[11px] uppercase text-muted-foreground text-right">Amount</th>
                <th className="px-4 py-2.5 font-mono text-[11px] uppercase text-muted-foreground">Status</th>
                <th></th>
              </tr></thead>
              <tbody>
                {cos.map(c => {
                  const v = project.vendors.find(x => x.id === c.vendor_id);
                  return (
                    <tr key={c.id} className="border-t border-border">
                      <td className="px-4 py-2 font-mono text-xs">{c.co_number}</td>
                      <td className="px-4 py-2">{v?.name || "—"}</td>
                      <td className="px-4 py-2 max-w-md truncate">{c.description}</td>
                      <td className="px-4 py-2 text-xs">{c.dated || "—"}</td>
                      <td className="px-4 py-2 text-right num font-bold">{fmtMoney(c.amount, false)}</td>
                      <td className="px-4 py-2">
                        <Select value={c.status} onValueChange={v => setStatus(c.id, v)}>
                          <SelectTrigger className="h-7 w-[110px] text-xs"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="pending">Pending</SelectItem>
                            <SelectItem value="approved">Approved</SelectItem>
                            <SelectItem value="rejected">Rejected</SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="px-4 py-2 text-right"><Button size="sm" variant="ghost" onClick={() => remove(c.id)}><Trash2 className="h-3.5 w-3.5" /></Button></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
    </div>
  );
}

/* -------------------- SNAG LIST -------------------- */
function SnagsTab({ project, catalogs }) {
  const { data: snags, mutate } = useSWR(`/projects/${project.id}/snags`, () => listSnags(project.id));
  const rooms = catalogs?.rooms || [];
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ room: "", description: "", priority: "medium", status: "open", assigned_to: "" });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!form.description.trim()) { toast.error("Describe the snag"); return; }
    setSaving(true);
    try {
      await createSnag(project.id, form);
      toast.success("Added");
      setOpen(false);
      setForm({ room: "", description: "", priority: "medium", status: "open", assigned_to: "" });
      mutate();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  const setStatus = async (id, status) => { try { await updateSnag(id, { status }); mutate(); } catch { toast.error("Failed"); } };
  const remove = async (id) => { if (!window.confirm("Delete this snag?")) return; try { await deleteSnag(id); mutate(); } catch { toast.error("Failed"); } };

  const open_ = (snags || []).filter(s => s.status === "open").length;
  const inProg = (snags || []).filter(s => s.status === "in_progress").length;
  const closed = (snags || []).filter(s => s.status === "closed").length;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="kpi-card card-glossy"><div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">Open</div><div className="num text-2xl font-bold mt-1 text-red-700">{open_}</div></div>
        <div className="kpi-card card-glossy"><div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">In progress</div><div className="num text-2xl font-bold mt-1 text-amber-700">{inProg}</div></div>
        <div className="kpi-card card-glossy"><div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">Closed</div><div className="num text-2xl font-bold mt-1 text-emerald-700">{closed}</div></div>
      </div>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="font-heading text-lg font-bold">Snag / punch list</h2>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="btn-add-snag"><Plus className="h-4 w-4 mr-1" /> Add snag</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Add snag</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Room</Label>
                <Select value={form.room || "unspec"} onValueChange={v => setForm(f => ({ ...f, room: v === "unspec" ? "" : v }))}>
                  <SelectTrigger><SelectValue placeholder="Choose room" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unspec">— unspecified —</SelectItem>
                    {rooms.map(o => <SelectItem key={o.id} value={o.value}>{o.value}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Description</Label><Textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="e.g. Skirting damage at door frame" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Priority</Label>
                  <Select value={form.priority} onValueChange={v => setForm(f => ({ ...f, priority: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Assigned to</Label><Input value={form.assigned_to} onChange={e => setForm(f => ({ ...f, assigned_to: e.target.value }))} placeholder="Vendor / person" /></div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={submit} disabled={saving}>{saving ? "Saving…" : "Save"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      {!snags ? <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
        : snags.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-border p-12 text-center bg-card/40">
            <ClipboardCheck className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
            <p className="text-muted-foreground">No snags recorded. Add items during pre-handover walkthrough.</p>
          </div>
        ) : (
          <div className="rounded-2xl border border-border bg-card/70 backdrop-blur-xl overflow-hidden card-glossy">
            <table className="w-full text-sm">
              <thead className="bg-muted/40"><tr className="text-left">
                <th className="px-4 py-2.5 font-mono text-[11px] uppercase text-muted-foreground">Room</th>
                <th className="px-4 py-2.5 font-mono text-[11px] uppercase text-muted-foreground">Description</th>
                <th className="px-4 py-2.5 font-mono text-[11px] uppercase text-muted-foreground">Priority</th>
                <th className="px-4 py-2.5 font-mono text-[11px] uppercase text-muted-foreground">Assigned</th>
                <th className="px-4 py-2.5 font-mono text-[11px] uppercase text-muted-foreground">Status</th>
                <th></th>
              </tr></thead>
              <tbody>
                {snags.map(s => (
                  <tr key={s.id} className="border-t border-border">
                    <td className="px-4 py-2">{s.room || "—"}</td>
                    <td className="px-4 py-2 max-w-md">{s.description}</td>
                    <td className="px-4 py-2">
                      <Badge variant="outline" className={`text-[10px] ${s.priority === "high" ? "bg-red-500/10 text-red-700 border-red-500/40" : s.priority === "medium" ? "bg-amber-500/10 text-amber-700 border-amber-500/40" : "bg-slate-500/10 text-slate-700 border-slate-500/40"}`}>{humanize(s.priority)}</Badge>
                    </td>
                    <td className="px-4 py-2 text-xs text-muted-foreground">{s.assigned_to || "—"}</td>
                    <td className="px-4 py-2">
                      <Select value={s.status} onValueChange={v => setStatus(s.id, v)}>
                        <SelectTrigger className="h-7 w-[130px] text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="open">Open</SelectItem>
                          <SelectItem value="in_progress">In progress</SelectItem>
                          <SelectItem value="closed">Closed</SelectItem>
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-4 py-2 text-right"><Button size="sm" variant="ghost" onClick={() => remove(s.id)}><Trash2 className="h-3.5 w-3.5" /></Button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
    </div>
  );
}

/* -------------------- EDIT PROJECT DIALOG -------------------- */
function EditProjectDialog({ open, setOpen, project, catalogs, onSaved }) {
  const [form, setForm] = useState(project);
  React.useEffect(() => setForm(project), [project]);
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    setSaving(true);
    try {
      await updateProject(project.id, {
        name: form.name, client_name: form.client_name, client_email: form.client_email,
        client_phone: form.client_phone, project_type: form.project_type, phase: form.phase,
        location: form.location, address: form.address, notes: form.notes,
        contract_value: Number(form.contract_value) || 0, status: form.status,
      });
      toast.success("Saved");
      setOpen(false);
      onSaved();
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button variant="outline" size="sm" data-testid="btn-edit-project"><Edit3 className="h-4 w-4 mr-1" /> Edit project</Button></DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Edit project</DialogTitle></DialogHeader>
        <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
          <div><Label>Name</Label><Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Type</Label>
              <Select value={form.project_type} onValueChange={v => setForm(f => ({ ...f, project_type: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{(catalogs?.project_types || []).map(o => <SelectItem key={o.id} value={o.value}>{o.value}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Phase</Label>
              <Select value={form.phase} onValueChange={v => setForm(f => ({ ...f, phase: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{(catalogs?.phases || []).map(o => <SelectItem key={o.id} value={o.value}>{o.value}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Client name</Label><Input value={form.client_name} onChange={e => setForm(f => ({ ...f, client_name: e.target.value }))} /></div>
            <div><Label>Client phone</Label><Input value={form.client_phone} onChange={e => setForm(f => ({ ...f, client_phone: e.target.value }))} /></div>
          </div>
          <div><Label>Client email</Label><Input value={form.client_email} onChange={e => setForm(f => ({ ...f, client_email: e.target.value }))} /></div>
          <div><Label>Address</Label><Input value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Location</Label><Input value={form.location} onChange={e => setForm(f => ({ ...f, location: e.target.value }))} /></div>
            <div><Label>Contract value</Label><Input type="number" value={form.contract_value} onChange={e => setForm(f => ({ ...f, contract_value: e.target.value }))} /></div>
          </div>
          <div><Label>Notes</Label><Textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving}>{saving ? "Saving…" : "Save changes"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
